import { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  TableContainer,
  TablePagination,
  Grid,
  Menu,
  Box,
  CardHeader,
  CardContent,
  Paper,
} from '@mui/material';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
// sections
import { UserListHead } from '../../../sections/@dashboard/user';

import { Page } from '../../../components';
import { getCategories } from '../store/categoryActions';
import { AssetCategoryForm, DeleteCategory } from '../components';
import { fDate } from '../../../utils/formatTime';
import useUrlFilters from '../../../hooks/useUrlFilters';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'class_code', label: 'Code', alignRight: false },
  { id: 'depreciation_rate', label: 'Depreciation Rate', alignRight: false },
  { id: 'created_at', label: 'Created', alignRight: false },
  // { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

// Module scope on purpose: useUrlFilters memoises on this object's identity.
const EMPTY_CATEGORY_FILTERS = { searchTerm: '' };

export default function Categories() {
  //
  const dispatch = useDispatch();
  const { isLoading, categories, pagination } = useSelector((state) => state.categoryReducer);
  //
  const [open, setOpen] = useState(null);

  // The search and the page live in the URL, so a refresh comes back to the
  // same rows.
  const { filters, hasFilters, page, rowsPerPage, setPage, setRowsPerPage, applyFilters, clearFilters } =
    useUrlFilters(EMPTY_CATEGORY_FILTERS, { defaultRowsPerPage: 10 });

  // What is being typed, as opposed to what is being searched on.
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm);

  useEffect(() => {
    setSearchTerm(filters.searchTerm);
  }, [filters]);

  const runSearch = () => {
    if (searchTerm === filters.searchTerm) return;
    applyFilters({ searchTerm });
  };

  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showCustomerEditModal, setShowCustomerEditModal] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleBack = () => setShowCustomerForm(false);
  const handleDialog = () => setOpenDialog(false);

  // const handleButtonClick = () => {
  //   setShowCustomerForm(true);
  // };

  const showCustomerEditModalHandler = () => setShowCustomerEditModal(false);

  const openSelectedRow = (customer, type) => {
    // eslint-disable-next-line no-unused-expressions
    type === 'edit' ? setShowCustomerEditModal(true) : setOpenDialog(true);
    setSelectedRow(customer);
  };

  useEffect(() => {
    dispatch(getCategories({ page: page + 1, rowsPerPage, ...filters }));
  }, [dispatch, page, rowsPerPage, filters]);

  return (
    <Page title="Categories">
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          {showCustomerForm ? (
            <Grid item xs={12} md={12} lg={12}>
              <AssetCategoryForm handleBack={handleBack} page={page} rowsPerPage={rowsPerPage} />
            </Grid>
          ) : (
            <>
              {/* <Grid item xs={12} md={4} lg={4}>
                  <Paper elevation={3} sx={{ py: 2, textAlign: 'center' }}>
                    <Box sx={{ mb: 0.5 }}>
                      <PeopleIcon />
                    </Box>
                    <Typography variant="h6">{fNumber(pagination?.count)}</Typography>
                    <Typography variant="body2">Categories</Typography>
                  </Paper>
                </Grid> */}
              <Grid item xs={12} md={12} lg={12}>
                <Card>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" sx={{ float: 'right' }} spacing={1}>
                      <TextField
                        size="small"
                        sx={{ minWidth: { xs: 180, sm: 260 } }}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        onBlur={runSearch}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') runSearch();
                        }}
                        placeholder="Search category name or code..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {hasFilters && (
                        <Button size="small" variant="outlined" onClick={clearFilters}>
                          Clear
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setShowCustomerForm(true)}
                        startIcon={<Iconify icon="eva:plus-fill" />}
                      >
                        New Category
                      </Button>
                    </Stack>
                  </Box>
                  <CardHeader title={'Categories'} subheader={'Asset categories'} />
                  <CardContent>
                    <Scrollbar>
                      <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                        {isLoading ? (
                          <Grid container sx={{ justifyContent: 'center' }}>
                            <ThreeDots
                              height="80"
                              width="80"
                              radius="9"
                              color="#2065d1"
                              ariaLabel="three-dots-loading"
                              wrapperStyle={{}}
                              wrapperClassName=""
                              visible
                            />
                          </Grid>
                        ) : (
                          <Table size="small">
                            <UserListHead headLabel={TABLE_HEAD} rowCount={categories?.length} />
                            <TableBody>
                              {categories?.length > 0 ? (
                                categories.map((category) => (
                                  <TableRow hover key={category?.id} tabIndex={-1} role="checkbox">
                                    <TableCell component="th" scope="row" padding="checkbox" sx={{ pl: 2 }}>
                                      <Stack direction="row" alignItems="center" spacing={2}>
                                        <Typography variant="subtitle2" noWrap>
                                          {category?.name}
                                        </Typography>
                                      </Stack>
                                    </TableCell>

                                    <TableCell align="left">{category?.class_code}</TableCell>
                                    <TableCell align="left">{category?.depreciation_rate}</TableCell>
                                    <TableCell align="left">{fDate(category?.created_at)}</TableCell>

                                    {/* <TableCell align="left">
                                        <Label color={(category?.status === 'INACTIVE' && 'error') || 'success'}>
                                          {sentenceCase(category?.status)}
                                        </Label>
                                      </TableCell> */}
                                    <TableCell>
                                      <PopupState
                                        variant="popover"
                                        popupId="category-popup-menu"
                                        sx={{
                                          p: 1,
                                          width: 140,
                                          '& .MuiMenuItem-root': {
                                            px: 1,
                                            typography: 'body2',
                                            borderRadius: 1,
                                          },
                                        }}
                                      >
                                        {(popupState) => (
                                          <>
                                            <IconButton
                                              size="large"
                                              color="inherit"
                                              aria-label="more"
                                              id="long-button"
                                              aria-controls={open ? 'long-menu' : undefined}
                                              aria-expanded={open ? 'true' : undefined}
                                              aria-haspopup="true"
                                              onClick={handleOpenMenu}
                                              {...bindTrigger(popupState)}
                                            >
                                              <MoreVertIcon />
                                            </IconButton>
                                            <Menu {...bindMenu(popupState)}>
                                              <MenuItem
                                                onClick={() => {
                                                  popupState.close();
                                                  openSelectedRow(category, 'edit');
                                                }}
                                              >
                                                <EditIcon sx={{ mr: 2 }} />
                                                Edit
                                              </MenuItem>
                                              <MenuItem
                                                sx={{ color: 'error.main' }}
                                                onClick={() => {
                                                  popupState.close();
                                                  openSelectedRow(category, 'delete');
                                                }}
                                              >
                                                <DeleteOutlineIcon sx={{ mr: 2 }} />
                                                Delete
                                              </MenuItem>
                                            </Menu>
                                          </>
                                        )}
                                      </PopupState>
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      No categories found
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        )}
                      </TableContainer>
                    </Scrollbar>

                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      component="div"
                      count={pagination?.count}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                    {showCustomerEditModal && (
                      <AssetCategoryForm
                        category={selectedRow}
                        open={showCustomerEditModal}
                        handleClose={showCustomerEditModalHandler}
                        page={page}
                        rowsPerPage={rowsPerPage}
                      />
                    )}
                    {openDialog && (
                      <DeleteCategory
                        open={openDialog}
                        handleClose={handleDialog}
                        category={selectedRow}
                        page={page}
                        rowsPerPage={rowsPerPage}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </Page>
  );
}
